import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import NaptimeResource from './NaptimeResource';

type CourseBranchItemIds = {
  courseId: string;
  branchId: string;
  itemId: string;
};

export type CheatingCaseRuling = 'CHEATING_NOT_CONFIRMED' | 'CHEATING_CONFIRMED' | 'RESOLVED_NO_RULING';

export type CheatingCaseStaffComment = {
  accused: {
    'org.coursera.integrity.SingleUser':
      | {
          userId: string;
        }
      | {
          'org.coursera.integrity.AccusedTeam': {
            teamId: string;
            guiltyMembers: Array<number>;
          };
        };
  };
  author: string;
  comment: string;
  createdAt: number;
  id: string;
  ruling: { ruling?: CheatingCaseRuling };
  submissionKey: string;
  courseItemContainer: {
    courseId: string;
    branchId: string;
    itemId: string;
  };
  userNameMap: { [userId: string]: string };
};

export const CHEATING_CASE_RULINGS = {
  RESOLVED_NO_CHEATING: 'CHEATING_NOT_CONFIRMED',
  RESOLVED_CHEATING: 'CHEATING_CONFIRMED',
  RESOLVED_NO_RULING: 'RESOLVED_NO_RULING',
  UNRESOLVED: undefined,
} as const;

const fields = [
  'createdAt',
  'accused',
  'author',
  'comment',
  'ruling',
  'submissionKey',
  'courseItemContainer',
  'userNameMap',
];

class CheatingCaseStaffCommentsResource extends NaptimeResource {
  static RESOURCE_NAME = 'cheatingCaseStaffComments.v1';

  static byAccusedCourseBranchAndItem({
    accusedId,
    courseId,
    branchId,
    itemId,
  }: {
    accusedId: string;
  } & CourseBranchItemIds) {
    return this.finder('byAccusedCourseBranchAndItem', {
      params: {
        accusedId,
        courseId,
        branchId,
        itemId,
      },
      fields,
    });
  }

  static byTeamCourseBranchAndItem(
    { scopedTeamId, courseId, branchId, itemId }: { scopedTeamId: string } & CourseBranchItemIds,
    options?: { required: boolean }
  ) {
    return this.finder('byTeamCourseBranchAndItem', {
      params: {
        scopedTeamId,
        courseId,
        branchId,
        itemId,
      },
      fields: [...fields, 'guiltyMembers'],
      required: options?.required ?? true,
    });
  }

  static byUserCourseBranchAndItem(
    { userId, courseId, branchId, itemId }: { userId: string } & CourseBranchItemIds,
    options?: { required: boolean }
  ) {
    return this.finder('byUserCourseBranchAndItem', {
      params: {
        userId,
        courseId,
        branchId,
        itemId,
      },
      fields,
      required: options?.required ?? true,
    });
  }

  static canStaffComment({
    userId,
    teamId,
    courseId,
    branchId,
    itemId,
  }: {
    userId?: string;
    teamId?: string;
  } & CourseBranchItemIds) {
    return this.action('canStaffComment', {
      // @ts-expect-error TSMIGRATION
      accusedId: teamId ? tupleToStringKey(['TEAM', teamId]) : tupleToStringKey(['USER', userId.toString()]),
      courseItemContainer: {
        courseId,
        branchId,
        itemId,
      },
    });
  }

  static resolutionByAccusedCourseBranchAndItem({
    userId,
    teamId,
    itemId,
    courseId,
    branchId,
  }: {
    userId?: string;
    teamId?: string;
  } & CourseBranchItemIds) {
    return this.action('resolutionByAccusedCourseBranchAndItem', {
      // @ts-expect-error TSMIGRATION
      accusedId: teamId ? tupleToStringKey(['TEAM', teamId]) : tupleToStringKey(['USER', userId]),
      courseItemContainer: {
        courseId,
        branchId,
        itemId,
      },
    });
  }
}

export default CheatingCaseStaffCommentsResource;
